import React, { useRef, useState, useEffect, useLayoutEffect } from 'react'

const FRAME_LIMIT = 1675

const CURVE_FREQUENCY = 350
const CURVE_RESOLUTION = 600
const FLASH_FREQUENCY = 10
const NAV_BAR_HEIGHT = 66
const MOBILE_BOTTOM_BAR_HEIGHT = 54
const MOBILE_BOTTOM_BAR_BREAKPOINT = 1024

const DOT_RADIUS = 10
const DOT_STROKE = 2
const DOT_RING_RADIUS = 22
const DOT_ECHO_RADIUS = 30

const DEBOUNCE_DELAY = 1000

let frameCount = 0

interface Props {
  className?: string
}

export function Canvas({ className }: Props) {
  const [redraw, setRedraw] = useState(false)

  const [dimensions, setDimensions] = useState({
    height: window.innerHeight,
    width: window.innerWidth,
  })

  const canvasRef = useRef<HTMLCanvasElement>(null)

  useLayoutEffect(() => {
    const canvas = canvasRef.current
    if (canvas) {
      const context = canvas.getContext('2d')
      let animationFrameId: number

      const render = () => {
        draw(context, frameCount)

        if (frameCount <= FRAME_LIMIT) {
          frameCount += 1
          animationFrameId = window.requestAnimationFrame(render)
        } else {
          window.cancelAnimationFrame(animationFrameId)
        }

        if (redraw) {
          setRedraw(false)
        }
      }
      render()

      return () => {
        window.cancelAnimationFrame(animationFrameId)
      }
    }
  }, [draw, redraw])

  useEffect(() => {
    const debouncedHandleResize = debounce(() => {
      setDimensions({
        height: window.innerHeight,
        width: window.innerWidth,
      })
      setRedraw(true)
    }, DEBOUNCE_DELAY)

    window.addEventListener('resize', debouncedHandleResize)

    return () => {
      window.removeEventListener('resize', debouncedHandleResize)
    }
  })

  return <canvas className={className} ref={canvasRef} width={dimensions.width} height={dimensions.height} />
}

const debounce = (fn: any, ms: number) => {
  let timer: any
  return () => {
    clearTimeout(timer)
    timer = setTimeout(() => {
      fn.call()
    }, ms)
  }
}

const draw = (context: CanvasRenderingContext2D | null, frameCount: number) => {
  if (context) {
    const width = context.canvas.width
    const height = context.canvas.height
    context.clearRect(0, 0, width, height)
    context.save()
    drawDataQ(context, frameCount, renderedQData)
    drawData(context, frameCount, renderedData)
    context.restore()
  }
}

const drawData = (context: CanvasRenderingContext2D | null, frameCount: number, data: any) => {
  if (!context) return

  const width = context.canvas.width
  const height = context.canvas.height

  const bottomBarHeight = width > MOBILE_BOTTOM_BAR_BREAKPOINT ? 0 : MOBILE_BOTTOM_BAR_HEIGHT

  const { ps, qs, maxQ } = data

  const xs = ps
  const ys = qs

  const minX = Math.min(...xs)
  const maxX = Math.max(...xs)
  const maxY = maxQ

  context.beginPath()

  for (let i = 0; i < xs.length; i++) {
    const x = ((xs[i] - minX) / (maxX - minX)) * width
    const y =
      height -
      NAV_BAR_HEIGHT -
      bottomBarHeight -
      (ys[i] / maxY) * (height - NAV_BAR_HEIGHT - bottomBarHeight) +
      NAV_BAR_HEIGHT
    if (i === 0) {
      context.moveTo(x, y)
    } else {
      context.lineTo(x, y)
    }
  }

  context.lineWidth = 2
  context.strokeStyle = 'rgb(250, 250, 250)'
  context.stroke()
  context.save()

  const frame = Math.floor((qs.length * (Math.sin(frameCount / CURVE_FREQUENCY) + 1)) / 2)
  const x = ((xs[frame] - minX) / (maxX - minX)) * width
  const y =
    height -
    NAV_BAR_HEIGHT -
    bottomBarHeight -
    (ys[frame] / maxY) * (height - NAV_BAR_HEIGHT - bottomBarHeight) +
    NAV_BAR_HEIGHT

  drawPoint(context, frameCount, x, y)

  context.restore()
}

const drawDataQ = (context: CanvasRenderingContext2D | null, frameCount: number, data: any) => {
  if (!context) return

  const width = context.canvas.width
  const height = context.canvas.height

  const bottomBarHeight = width > MOBILE_BOTTOM_BAR_BREAKPOINT ? 0 : MOBILE_BOTTOM_BAR_HEIGHT

  const { ps, qss, maxQ } = data

  const frame = Math.floor((qss.length * (Math.sin(frameCount / CURVE_FREQUENCY) + 1)) / 2)

  const xs = ps
  const ys = qss[frame]

  const minX = Math.min(...xs)
  const maxX = Math.max(...xs)
  const maxY = maxQ

  context.beginPath()

  context.moveTo(width, 0)
  context.lineTo(0, 0)

  for (let i = 0; i < xs.length; i++) {
    const x = ((xs[i] - minX) / (maxX - minX)) * width
    const y =
      height -
      NAV_BAR_HEIGHT -
      bottomBarHeight -
      (ys[i] / maxY) * (height - NAV_BAR_HEIGHT - bottomBarHeight) +
      NAV_BAR_HEIGHT
    context.lineTo(x, y)
  }

  const gradient = context.createLinearGradient(0, 0, width, height)
  gradient.addColorStop(0, 'rgb(46,46,46)')
  gradient.addColorStop(1, 'rgb(75,89,117)')
  context.fillStyle = gradient
  context.fill()

  context.save()

  context.restore()
}

const linspace = (start: number, end: number, samples: number) => {
  const interval = (end - start) / samples
  const array = []
  for (let i = 0; i < samples; i++) {
    array.push(start + interval * i)
  }
  return array
}

const leverageUniswap = (x: number, l = 1, d = 1) => {
  const x0 = d ** 0.5
  const y0 = d ** 0.5
  return (l ** 2 * x0 * y0) / (x - (1 - l) * x0) + (1 - l) * y0
}

const leverageUniswapQ = (p: number, x1?: number, l = 1, d = 1) => {
  const x0 = d ** 0.5
  if (!x1) {
    x1 = x0
  }
  const x = l * Math.sqrt(d / p) + (1 - l) * x0
  const p1 = (d * l * l) / (x1 + (l - 1) * x0) ** 2
  if (p < p1) {
    return x - x1
  } else if (p > p1) {
    return x1 - x
  } else {
    return 0
  }
}

const leveragedUniswapData = () => {
  const xs = linspace(0, 3, CURVE_RESOLUTION + 1).slice(1)
  const ys2 = xs.reduce((memo: number[], x) => {
    if (x - (1 - 2) * 1 > 0) {
      memo.push(leverageUniswap(x, 2))
    }
    return memo
  }, [])

  return {
    ps: xs,
    qs: ys2,
    maxQ: Math.max(...ys2),
  }
}

const renderedData = leveragedUniswapData()

const leveragedUniswapQData = () => {
  const ps = linspace(1 / 4, 4, CURVE_RESOLUTION + 1)
  const x1s = linspace(0, 3, CURVE_RESOLUTION + 1).slice(1, -1)
  let maxQ = 0
  const qss = x1s.reduce((memo: number[][], x1) => {
    memo.push(
      ps.reduce((memo: number[], p) => {
        const q = leverageUniswapQ(p, x1, 2)
        if (q > maxQ) {
          maxQ = q
        }
        memo.push(q)
        return memo
      }, [])
    )
    return memo
  }, [])

  return {
    ps,
    qss,
    maxQ,
  }
}

const renderedQData = leveragedUniswapQData()

const drawPoint = (context: CanvasRenderingContext2D | null, frameCount: number, x: number, y: number) => {
  if (context) {
    const frame = (Math.sin(frameCount / FLASH_FREQUENCY) + 1) / 2

    const r = (DOT_RING_RADIUS - (DOT_RADIUS - DOT_STROKE)) * frame + (DOT_RADIUS - DOT_STROKE)

    context.beginPath()
    context.arc(x, y, r, 0, 2 * Math.PI, false)
    context.lineWidth = DOT_STROKE
    context.strokeStyle = 'rgba(250, 250, 250, 0.9)'
    context.stroke()
    context.save()
    context.restore()

    const re = (DOT_ECHO_RADIUS - (DOT_RADIUS - DOT_STROKE)) * frame + (DOT_RADIUS - DOT_STROKE)

    context.beginPath()
    context.arc(x, y, re, 0, 2 * Math.PI, false)
    context.lineWidth = 2
    context.strokeStyle = 'rgba(250, 250, 250, 0.5)'
    context.stroke()
    context.save()
    context.restore()

    context.beginPath()
    context.arc(x, y, DOT_RADIUS, 0, 2 * Math.PI, false)
    context.fillStyle = `rgb(${Math.round((141 - 35) * frame + 35)}, ${Math.round(
      (166 - 75) * frame + 75
    )}, ${Math.round((223 - 163) * frame + 163)})`
    context.fill()
    context.save()
    context.restore()
  }
}
